import { User } from '../contexts/AuthContext';
import { getLocalDateString } from '../src/lib/utils';

export type ExpenseType = string; 
export type ExpenseStatus = 'open' | 'paid' | 'overdue';
export type AttachmentKind = 'boleto' | 'comprovante' | 'outro';

export interface Attachment {
  id: string;
  expenseId: string;
  kind: AttachmentKind;
  downloadURL: string;
  thumbnailUrl?: string;
  name: string;
  uploadedAt: string;
  uploadedBy: string;
}

export interface Expense {
  id: string;
  type: string;
  title: string;
  description?: string;
  category: string;
  supplierName: string;
  amount: number;
  paidAmount?: number | null;
  dueDate?: string;
  paymentDate?: string | null;
  codigo_barras?: string;
  linha_digitavel?: string;
  pix?: string;
  status: ExpenseStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
  vehicle?: string | null;
  attachments: Attachment[];
  previsao_pagamento?: string;
}

export interface Revenue {
  id: string;
  cliente: string;
  descricao?: string;
  numero_nota?: string;
  serie_nota?: string;
  tipo_nota?: string;
  data_emissao?: string;
  previsao_pagamento?: string;
  data_recebimento?: string;
  valor_bruto: number;
  valor_desconto: number;
  impostos?: number;
  valor_liquido: number;
  forma_recebimento?: string;
  categoria_receita?: string;
  centro_receita?: string;
  observacoes?: string;
  status: 'EM_ABERTO' | 'RECEBIDO' | 'VENCIDO' | 'CANCELADO';
  createdBy: string;
  created_at: string;
  updated_at: string;
}

export interface QuoteItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface Quote {
  id: string;
  clientName: string;
  clientDoc?: string | null;
  date: string;
  purchaseDate?: string | null;
  quoteType?: string;
  description?: string;
  validityDate?: string;
  products: QuoteItem[];
  services: QuoteItem[];
  totalProducts: number;
  totalServices: number;
  grandTotal: number;
  status: 'draft' | 'sent' | 'approved' | 'rejected';
  notes?: string | null;
  createdBy: string;
  createdAt: string;
}

const recalculateStatus = (expense: Expense): ExpenseStatus => {
    if (expense.paymentDate) return 'paid';
    if (!expense.dueDate) return 'open';
    const today = getLocalDateString();
    if (expense.dueDate < today) return 'overdue';
    return 'open';
};

export const mockBackend = {
  uploadImage: async (base64String: string, name?: string, mimeType?: string): Promise<{ url: string, thumbnailUrl?: string }> => {
    try {
      const response = await fetch("/api/upload", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ base64String, fileName: name, mimeType }),
      });

      const result = await response.json();
      if (response.ok && result.url) {
        const isPdf = name?.toLowerCase().endsWith('.pdf') || mimeType === 'application/pdf';
        const isImage = mimeType?.startsWith('image/') || name?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i);
        
        let finalUrl = result.url;
        let finalThumbnailUrl = result.thumbnailUrl;
        
        if (result.id) {
          if (isPdf) {
            finalUrl = `https://drive.google.com/file/d/${result.id}/preview`;
            finalThumbnailUrl = undefined;
          } else if (isImage) {
            // Use lh3.googleusercontent.com for images as requested
            finalUrl = `https://lh3.googleusercontent.com/d/${result.id}=s0?authuser=0`;
            finalThumbnailUrl = `https://lh3.googleusercontent.com/d/${result.id}=w400?authuser=0`;
          } else {
            // Force download for other files
            finalUrl = `https://drive.google.com/uc?export=download&id=${result.id}`;
            finalThumbnailUrl = undefined;
          }
        }
        return { url: finalUrl, thumbnailUrl: finalThumbnailUrl };
      } else {
        throw new Error(result.error || "Erro ao fazer upload da imagem");
      }
    } catch (error: any) {
      console.error("Upload Error:", error);
      throw new Error("Falha no upload da imagem: " + error.message);
    }
  },

  subscribeToExpenses: (user: User | null, callback: (expenses: Expense[]) => void) => {
    let isSubscribed = true;
    const fetchExpenses = async () => {
      try {
        const expenses = await mockBackend.getExpenses();
        if (isSubscribed) callback(expenses);
      } catch (e) {
        console.error(e);
      }
    };
    fetchExpenses();
    const interval = setInterval(fetchExpenses, 5000);
    return () => {
      isSubscribed = false;
      clearInterval(interval);
    };
  },

  createExpense: async (user: User, expense: any): Promise<Expense> => {
    return mockBackend.addExpense(user, expense);
  },

  updateCategory: async (user: User, oldName: string, newName: string): Promise<void> => {
    const categories = await mockBackend.getCategories();
    const newCategories = categories.map(c => c === oldName ? newName : c);
    await fetch('/api/settings/categories', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: newCategories })
    });
  },

  updateType: async (user: User, oldName: string, newName: string): Promise<void> => {
    const types = await mockBackend.getTypes();
    const newTypes = types.map(t => t === oldName ? newName : t);
    await fetch('/api/settings/types', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: newTypes })
    });
  },

  updateVehicle: async (user: User, oldName: string, newName: string): Promise<void> => {
    const vehicles = await mockBackend.getVehicles();
    const newVehicles = vehicles.map(v => v === oldName ? newName : v);
    await fetch('/api/settings/vehicles', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: newVehicles })
    });
  },

  createUser: async (user: User, data: any): Promise<User> => {
    return mockBackend.addUser(user, data);
  },

  changePassword: async (user: User, id: string, newPass: string): Promise<void> => {
    return mockBackend.updateUserPassword(id, newPass);
  },

  getAllUsersInternal: async () => {
    const res = await fetch('/api/users');
    if (!res.ok) throw new Error('Failed to fetch users');
    return await res.json();
  },

  getUsers: async (user?: User | null): Promise<User[]> => {
    const res = await fetch('/api/users');
    if (!res.ok) throw new Error('Failed to fetch users');
    return await res.json();
  },

  addUser: async (user: User | null, data: Omit<User, 'id'>): Promise<User> => {
    const newUser = { ...data, id: crypto.randomUUID() };
    const res = await fetch('/api/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newUser)
    });
    if (!res.ok) throw new Error('Failed to add user');
    return newUser as User;
  },

  updateUser: async (user: User | null, id: string, data: Partial<User>): Promise<void> => {
    const res = await fetch(`/api/users/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update user');
  },

  updateUserPassword: async (id: string, newPassword: string): Promise<void> => {
    const res = await fetch(`/api/users/${id}/password`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: newPassword })
    });
    if (!res.ok) throw new Error('Failed to update password');
  },

  deleteUser: async (user: User | null, id: string): Promise<void> => {
    const res = await fetch(`/api/users/${id}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Failed to delete user');
  },

  getCategories: async (user?: User | null): Promise<string[]> => {
    const res = await fetch('/api/settings');
    if (!res.ok) throw new Error('Failed to fetch settings');
    const data = await res.json();
    return data.categories || ['Infraestrutura', 'Marketing', 'Pessoal', 'Transporte', 'Serviços', 'Contabilidade'];
  },

  addCategory: async (user: User | null, category: string): Promise<void> => {
    const categories = await mockBackend.getCategories();
    if (!categories.includes(category)) {
      categories.push(category);
      await fetch('/api/settings/categories', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: categories })
      });
    }
  },

  deleteCategory: async (user: User | null, category: string): Promise<void> => {
    const categories = await mockBackend.getCategories();
    const newCategories = categories.filter(c => c !== category);
    await fetch('/api/settings/categories', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: newCategories })
    });
  },

  getTypes: async (user?: User | null): Promise<string[]> => {
    const res = await fetch('/api/settings');
    if (!res.ok) throw new Error('Failed to fetch settings');
    const data = await res.json();
    return data.types || ['Boleto', 'Gasolina', 'Compra Interna', 'Compra Externa', 'Reembolso', 'Despesas'];
  },

  addType: async (user: User | null, type: string): Promise<void> => {
    const types = await mockBackend.getTypes();
    if (!types.includes(type)) {
      types.push(type);
      await fetch('/api/settings/types', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: types })
      });
    }
  },

  deleteType: async (user: User | null, type: string): Promise<void> => {
    const types = await mockBackend.getTypes();
    const newTypes = types.filter(t => t !== type);
    await fetch('/api/settings/types', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: newTypes })
    });
  },

  getVehicles: async (user?: User | null): Promise<string[]> => {
    const res = await fetch('/api/settings');
    if (!res.ok) throw new Error('Failed to fetch settings');
    const data = await res.json();
    return data.vehicles || ['Fiat - Fiorino', 'Chevrolet - S10', 'Nissan - Frontier'];
  },

  addVehicle: async (user: User | null, vehicle: string): Promise<void> => {
    const vehicles = await mockBackend.getVehicles();
    if (!vehicles.includes(vehicle)) {
      vehicles.push(vehicle);
      await fetch('/api/settings/vehicles', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: vehicles })
      });
    }
  },

  deleteVehicle: async (user: User | null, vehicle: string): Promise<void> => {
    const vehicles = await mockBackend.getVehicles();
    const newVehicles = vehicles.filter(v => v !== vehicle);
    await fetch('/api/settings/vehicles', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: newVehicles })
    });
  },

  getSuppliers: async (): Promise<string[]> => {
    const res = await fetch('/api/expenses');
    if (!res.ok) throw new Error('Failed to fetch expenses');
    const expenses = await res.json();
    const suppliers = new Set(expenses.map((e: any) => e.supplierName).filter(Boolean));
    return Array.from(suppliers) as string[];
  },

  getCostCenters: async (): Promise<string[]> => {
    return ['TI', 'Vendas', 'RH', 'Diretoria'];
  },

  getExpenses: async (user?: User | null): Promise<Expense[]> => {
    const res = await fetch('/api/expenses');
    if (!res.ok) throw new Error('Failed to fetch expenses');
    const expenses = await res.json();
    return expenses.map((e: any) => ({
      ...e,
      status: recalculateStatus(e),
      dueDate: e.dueDate ? e.dueDate.split('T')[0] : undefined,
      paymentDate: e.paymentDate ? e.paymentDate.split('T')[0] : undefined,
    }));
  },

  addExpense: async (user: User | null, expense: Omit<Expense, 'id' | 'createdAt' | 'updatedAt'>): Promise<Expense> => {
    // Verificar duplicidade antes de criar
    const allExpenses = await mockBackend.getExpenses(user);
    const isDuplicate = allExpenses.some(e => 
      e.supplier === expense.supplier && 
      e.amount === expense.amount && 
      e.dueDate === expense.dueDate &&
      e.type === expense.type &&
      e.category === expense.category
    );
    
    if (isDuplicate) {
      throw new Error("DADO_DUPLICADO: Já existe um registro com este mesmo fornecedor/cliente, valor e vencimento.");
    }
    
    const newExpense = {
      ...expense,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: recalculateStatus(expense as Expense),
    };
    const res = await fetch('/api/expenses', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newExpense)
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to add expense');
    }
    return newExpense as Expense;
  },

  updateExpense: async (user: User | null, id: string, updates: Partial<Expense>): Promise<Expense> => {
    const res = await fetch('/api/expenses');
    const expenses = await res.json();
    const existing = expenses.find((e: any) => e.id === id);
    if (!existing) throw new Error('Expense not found');

    const updatedExpense = {
      ...existing,
      ...updates,
      updatedAt: new Date().toISOString(),
    };
    updatedExpense.status = recalculateStatus(updatedExpense);

    const updateRes = await fetch(`/api/expenses/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedExpense)
    });
    if (!updateRes.ok) {
      const errorData = await updateRes.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to update expense');
    }
    return updatedExpense;
  },

  deleteExpense: async (user: User | null, id: string): Promise<void> => {
    const res = await fetch(`/api/expenses/${id}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Failed to delete expense');
  },

  addAttachment: async (user: User | null, expenseId: string, downloadURL: string, fileName: string, kind: AttachmentKind, uploadedBy?: string, thumbnailUrl?: string): Promise<Attachment> => {
    const newAttachment: Attachment = {
      id: crypto.randomUUID(),
      expenseId,
      kind,
      downloadURL,
      thumbnailUrl,
      name: fileName,
      uploadedAt: new Date().toISOString(),
      uploadedBy,
    };
    
    const res = await fetch('/api/attachments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newAttachment)
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to add attachment');
    }
    return newAttachment;
  },

  deleteAttachment: async (user: User | null, attachmentId: string): Promise<void> => {
    const res = await fetch(`/api/attachments/${attachmentId}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Failed to delete attachment');
  },

  getQuotes: async (): Promise<Quote[]> => {
    const res = await fetch('/api/quotes');
    if (!res.ok) throw new Error('Failed to fetch quotes');
    const quotes = await res.json();
    return quotes.map((q: any) => ({
      ...q,
      date: q.date ? q.date.split('T')[0] : undefined,
      purchaseDate: q.purchaseDate ? q.purchaseDate.split('T')[0] : undefined,
      validityDate: q.validityDate ? q.validityDate.split('T')[0] : undefined,
    }));
  },

  getRevenues: async (user?: User | null): Promise<Revenue[]> => {
    const res = await fetch('/api/revenues');
    if (!res.ok) throw new Error('Failed to fetch revenues');
    const revenues = await res.json();
    return revenues.map((r: any) => ({
      ...r,
      data_emissao: r.data_emissao ? r.data_emissao.split('T')[0] : undefined,
      previsao_pagamento: r.previsao_pagamento ? r.previsao_pagamento.split('T')[0] : undefined,
      data_recebimento: r.data_recebimento ? r.data_recebimento.split('T')[0] : undefined,
      created_at: r.created_at ? r.created_at.split('T')[0] : undefined,
      updated_at: r.updated_at ? r.updated_at.split('T')[0] : undefined,
    }));
  },

  addRevenue: async (user: User | null, revenue: Omit<Revenue, 'id' | 'created_at' | 'updated_at'>): Promise<Revenue> => {
    // Verificar duplicidade antes de criar
    const allRevenues = await mockBackend.getRevenues(user);
    const isDuplicate = allRevenues.some(r => 
      r.client_name === revenue.client_name && 
      r.amount === revenue.amount && 
      r.due_date === revenue.due_date &&
      r.type === revenue.type &&
      r.category === revenue.category
    );
    
    if (isDuplicate) {
      throw new Error("DADO_DUPLICADO: Já existe um registro com este mesmo fornecedor/cliente, valor e vencimento.");
    }

    const newRevenue = {
      ...revenue,
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    const res = await fetch('/api/revenues', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newRevenue)
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to add revenue');
    }
    return newRevenue as Revenue;
  },

  updateRevenue: async (user: User | null, id: string, updates: Partial<Revenue>): Promise<Revenue> => {
    const res = await fetch('/api/revenues');
    const revenues = await res.json();
    const existing = revenues.find((r: any) => r.id === id);
    if (!existing) throw new Error('Revenue not found');

    const updatedRevenue = {
      ...existing,
      ...updates,
      updated_at: new Date().toISOString(),
    };

    const updateRes = await fetch(`/api/revenues/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedRevenue)
    });
    if (!updateRes.ok) {
      const errorData = await updateRes.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to update revenue');
    }
    return updatedRevenue;
  },

  deleteRevenue: async (user: User | null, id: string): Promise<void> => {
    const res = await fetch(`/api/revenues/${id}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Failed to delete revenue');
  },

  subscribeToRevenues: (user: User | null, callback: (revenues: Revenue[]) => void) => {
    let isSubscribed = true;
    const fetchRevenues = async () => {
      try {
        const revenues = await mockBackend.getRevenues();
        if (isSubscribed) callback(revenues);
      } catch (e) {
        console.error(e);
      }
    };
    fetchRevenues();
    const interval = setInterval(fetchRevenues, 5000);
    return () => {
      isSubscribed = false;
      clearInterval(interval);
    };
  },

  addQuote: async (quote: Omit<Quote, 'id' | 'createdAt'>): Promise<Quote> => {
    const newQuote = {
      ...quote,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
    };
    const res = await fetch('/api/quotes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newQuote)
    });
    if (!res.ok) throw new Error('Failed to add quote');
    return newQuote as Quote;
  },

  updateQuote: async (id: string, updates: Partial<Quote>): Promise<Quote> => {
    const res = await fetch('/api/quotes');
    const quotes = await res.json();
    const existing = quotes.find((q: any) => q.id === id);
    if (!existing) throw new Error('Quote not found');

    const updatedQuote = {
      ...existing,
      ...updates,
    };

    const updateRes = await fetch(`/api/quotes/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedQuote)
    });
    if (!updateRes.ok) throw new Error('Failed to update quote');
    return updatedQuote;
  },

  deleteQuote: async (id: string): Promise<void> => {
    const res = await fetch(`/api/quotes/${id}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Failed to delete quote');
  },
};
